import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Home, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import MobileTopBar from "../components/MobileTopBar";
import StudioFooter from "../components/StudioFooter";
import { getOrder } from "../lib/api";
import { formatINR } from "../lib/pricing";

export default function OrderConfirmedPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getOrder(orderId);
        if (!cancelled) setOrder(data);
      } catch (e) {
        if (!cancelled) toast.error("Order not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(orderId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      /* noop */
    }
  };

  return (
    <div className="flex min-h-screen bg-brand-ink">
      <Sidebar />
      <main className="flex-1 overflow-x-hidden">
        <MobileTopBar title="Order" />
        <div className="mx-auto max-w-[900px] px-5 py-8 sm:px-8 sm:py-10">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            <Link to="/dashboard" className="inline-flex items-center gap-2 hover:text-brand">
              <Home className="h-3 w-3" /> Dashboard
            </Link>
            <span className="text-brand-mute/40">/</span>
            <span className="text-brand">Order Confirmed</span>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand/15 text-brand">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand">
                Payment successful
              </div>
              <h1 className="mt-1 font-serif-display text-[42px] font-medium leading-none tracking-tight">
                Order confirmed.
              </h1>
            </div>
          </div>

          {loading ? (
            <div className="mt-10 rounded-md border border-brand-hair bg-brand-panel px-6 py-10 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Loading order...
            </div>
          ) : !order ? (
            <div className="mt-10 rounded-md border border-brand-hair bg-brand-panel px-6 py-10 text-center">
              <div className="font-serif-display text-[22px]">Order not found.</div>
              <button onClick={() => navigate("/dashboard")} className="mt-4 text-brand">
                Back to Dashboard
              </button>
            </div>
          ) : (
            <>
              <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
                <MetaCard label="Order ID" value={order.id} onCopy={copy} copied={copied} />
                <MetaCard label="Razorpay Order" value={order.razorpay_order_id || "—"} />
                <MetaCard label="Status" value={(order.status || "paid").toUpperCase()} accent />
              </div>

              <div className="mt-8 overflow-hidden rounded-xl border border-brand-hair bg-brand-panel">
                <div className="border-b border-brand-hair px-6 py-4 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Items ({order.items.length})
                </div>
                <ul className="divide-y divide-brand-hair/60">
                  {order.items.map((it, i) => (
                    <li key={i} className="grid grid-cols-[70px_1fr_auto] items-center gap-4 px-6 py-4">
                      <div className="font-mono text-[10.5px] tracking-[0.22em] text-brand">{it.code}</div>
                      <div className="min-w-0">
                        <div className="truncate font-serif-display text-[16px] leading-tight text-brand-ivory">{it.title}</div>
                        <div className="truncate font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand-mute">
                          {it.service || ""} {it.billing ? `· ${it.billing}` : ""}
                        </div>
                      </div>
                      <div className="font-mono text-[13px] text-brand-ivory">{formatINR(it.price_amount)}</div>
                    </li>
                  ))}
                </ul>
                <div className="space-y-2 border-t border-brand-hair px-6 py-5 text-[13px]">
                  <Row label="Subtotal" value={formatINR(order.subtotal)} />
                  <Row label="GST @ 18%" value={formatINR(order.gst_amount)} muted />
                  <div className="my-2 h-px bg-brand-hair" />
                  <div className="flex items-baseline justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Paid</div>
                    <div className="font-serif-display text-[26px] text-brand">{formatINR(order.total_amount)}</div>
                  </div>
                </div>
              </div>

              <p className="mt-6 text-[13px] text-brand-ivory/60">
                A studio editor will confirm scope for every item in your order within 24 hours at
                <span className="text-brand-ivory"> {order.customer_email}</span>.
                {order.customer_gst && (
                  <> Invoice will be raised against GST <span className="font-mono text-brand-ivory">{order.customer_gst}</span>.</>
                )}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-3 font-mono text-[11px] uppercase tracking-[0.28em] text-white hover:bg-[#ff5c26]">
                  Back to studio <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </>
          )}

          <StudioFooter />
        </div>
      </main>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex items-baseline justify-between ${muted ? "text-brand-ivory/55" : "text-brand-ivory/85"}`}>
      <div className="text-[12.5px]">{label}</div>
      <div className="font-mono text-[12.5px]">{value}</div>
    </div>
  );
}

function MetaCard({ label, value, onCopy, copied, accent }) {
  return (
    <div className="rounded-xl border border-brand-hair bg-brand-panel p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">{label}</div>
      <div className={`mt-2 flex items-center justify-between gap-2 font-mono text-[12.5px] ${accent ? "text-brand" : "text-brand-ivory"}`}>
        <span className="truncate">{value}</span>
        {onCopy && (
          <button onClick={onCopy} className="flex h-6 w-6 items-center justify-center rounded-md border border-brand-hair hover:border-brand">
            {copied ? <Check className="h-3 w-3 text-brand" /> : <Copy className="h-3 w-3 text-brand-mute" />}
          </button>
        )}
      </div>
    </div>
  );
}
